import {
  all,
  call,
  put,
  select,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";
import api from "../../utils/api";
import { errorAlert, successAlert } from "../Alerts/actions";
import {
  fetchCareHearthBuyer,
  fetchCareHearthBuyerFailure,
  fetchCareHearthBuyersFailure,
  fetchCareHearthBuyersSuccess,
  fetchCareHearthBuyerSuccess,
  fetchCareHearthDisputes,
  fetchCareHearthDisputesFailure,
  fetchCareHearthDisputesSuccess,
  fetchCareHearthInvoices,
  fetchCareHearthInvoicesFailure,
  fetchCareHearthInvoicesSuccess,
  markCareHearthInvoicePaidFailure,
  markCareHearthInvoicePaidSuccess,
  raiseCareHearthDisputesFailure,
  raiseCareHearthDisputesSuccess,
  recalculateCareHearthBuyerFailure,
  recalculateCareHearthBuyerSuccess,
} from "./actions";
import {
  FETCH_CARE_HEARTH_BUYER,
  FETCH_CARE_HEARTH_BUYERS,
  FETCH_CARE_HEARTH_DISPUTES,
  FETCH_CARE_HEARTH_INVOICES,
  MARK_CARE_HEARTH_INVOICE_PAID,
  RAISE_CARE_HEARTH_DISPUTES,
  RECALCULATE_CARE_HEARTH_BUYER,
} from "./constants";

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string") return error;
  return error?.error || error?.message || fallback;
};

const getToken = (state) => state?.AuthReducer?.token;

export function* fetchCareHearthBuyersSaga({ payload }) {
  const token = yield select(getToken);

  try {
    const response = yield call(api(token).get, "/api/admin/hearth/buyers", {
      params: payload,
    });
    yield put(
      fetchCareHearthBuyersSuccess({
        items: response?.data?.items || [],
        pagination: response?.data?.pagination || { last_page: 1, total: 0 },
        summary: response?.data?.summary || null,
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthBuyersFailure(
        getErrorMessage(error, "Could not load the CareHearth buyers."),
      ),
    );
  }
}

export function* fetchCareHearthBuyerSaga({ payload: { locationId } }) {
  const token = yield select(getToken);

  try {
    const response = yield call(
      api(token).get,
      `/api/admin/hearth/buyers/${locationId}`,
    );
    yield put(fetchCareHearthBuyerSuccess(response?.data || null));
  } catch (error) {
    yield put(
      fetchCareHearthBuyerFailure(
        getErrorMessage(error, "Could not load this buyer's standing."),
      ),
    );
  }
}

export function* recalculateCareHearthBuyerSaga({ payload: { locationId } }) {
  const token = yield select(getToken);

  try {
    yield call(
      api(token).post,
      `/api/admin/hearth/buyers/${locationId}/recalculate`,
    );
    yield put(successAlert("Buyer standing recalculated."));
    yield put(recalculateCareHearthBuyerSuccess());
    yield put(fetchCareHearthBuyer({ locationId }));
  } catch (error) {
    const message = getErrorMessage(error, "Could not recalculate standing.");
    yield put(errorAlert(message));
    yield put(recalculateCareHearthBuyerFailure(message));
  }
}

// Auto-buy invoices for every home (15 October 2026), filtered by status,
// month ("YYYY-MM") and a search on the home or company.
export function* fetchCareHearthInvoicesSaga({ payload }) {
  const token = yield select(getToken);

  try {
    const response = yield call(api(token).get, "/api/admin/hearth/invoices", {
      params: payload,
    });
    yield put(
      fetchCareHearthInvoicesSuccess({
        items: response?.data?.items || [],
        counts: response?.data?.counts || null,
        meta: response?.data?.meta || {
          current_page: 1,
          last_page: 1,
          total: 0,
        },
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthInvoicesFailure(
        getErrorMessage(error, "Could not load the auto-buy invoices."),
      ),
    );
  }
}

// Money that reached us outside the Stripe invoice page. Stripe records the
// invoice as paid out of band; the API resumes auto-buy if it was paused for
// an overdue invoice and no other invoice is overdue.
export function* markCareHearthInvoicePaidSaga({
  payload: { uuid, locationId, params },
}) {
  const token = yield select(getToken);

  try {
    yield call(api(token).post, `/api/admin/hearth/invoices/${uuid}/mark-paid`);
    yield put(successAlert("Invoice marked as paid."));
    yield put(markCareHearthInvoicePaidSuccess());
    if (locationId) yield put(fetchCareHearthBuyer({ locationId }));
    if (params) yield put(fetchCareHearthInvoices(params));
  } catch (error) {
    const message = getErrorMessage(
      error,
      "Could not mark this invoice as paid.",
    );
    yield put(errorAlert(message));
    yield put(markCareHearthInvoicePaidFailure(message));
  }
}

// The Disputes list (15 October 2026): a month's disputes for every home or
// one, with that month's figures.
export function* fetchCareHearthDisputesSaga({ payload }) {
  const token = yield select(getToken);

  try {
    const response = yield call(api(token).get, "/api/admin/hearth/disputes", {
      params: payload,
    });
    yield put(
      fetchCareHearthDisputesSuccess({
        items: response?.data?.items || [],
        summary: response?.data?.summary || null,
        meta: response?.data?.meta || {
          current_page: 1,
          last_page: 1,
          total: 0,
        },
        canRaise: Boolean(response?.data?.can_raise),
        reasons: response?.data?.reasons || {},
      }),
    );
  } catch (error) {
    yield put(
      fetchCareHearthDisputesFailure(
        getErrorMessage(error, "Could not load the disputes."),
      ),
    );
  }
}

// Our team raises a dispute for a home on the enquiries it picked, with one
// reason and note. An enquiry that can no longer be reported is said back.
export function* raiseCareHearthDisputesSaga({ payload: { body, params } }) {
  const token = yield select(getToken);

  try {
    const response = yield call(
      api(token).post,
      "/api/admin/hearth/disputes",
      body,
    );
    const raised = response?.data?.raised?.length || 0;
    const failed = response?.data?.failed || [];
    yield put(
      successAlert(
        `${raised === 1 ? "Dispute raised" : `${raised} disputes raised`}.${
          failed.length
            ? ` ${failed.length} could not be: ${failed[0].message}`
            : ""
        }`,
      ),
    );
    yield put(raiseCareHearthDisputesSuccess());
    if (params) yield put(fetchCareHearthDisputes(params));
  } catch (error) {
    const message = getErrorMessage(error, "Could not raise the dispute.");
    yield put(errorAlert(message));
    yield put(raiseCareHearthDisputesFailure(message));
  }
}

export default function* CareHearthBuyersSaga() {
  yield all([
    takeLatest(FETCH_CARE_HEARTH_BUYERS, fetchCareHearthBuyersSaga),
    takeLatest(FETCH_CARE_HEARTH_BUYER, fetchCareHearthBuyerSaga),
    takeLatest(RECALCULATE_CARE_HEARTH_BUYER, recalculateCareHearthBuyerSaga),
    takeLatest(FETCH_CARE_HEARTH_INVOICES, fetchCareHearthInvoicesSaga),
    takeEvery(MARK_CARE_HEARTH_INVOICE_PAID, markCareHearthInvoicePaidSaga),
    takeLatest(FETCH_CARE_HEARTH_DISPUTES, fetchCareHearthDisputesSaga),
    takeEvery(RAISE_CARE_HEARTH_DISPUTES, raiseCareHearthDisputesSaga),
  ]);
}
